import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiSearch, FiUser } from 'react-icons/fi';
import { useAuth } from '../../../hooks/useAuth';

const UserSelectPopover = ({ users, onSelect, onClose, position, currentAssignee }) => {
    const { user } = useAuth();
    const popoverRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const filteredUsers = users.filter(u =>
        u.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const content = (
        <div
            ref={popoverRef}
            style={{ position: 'fixed', top: position.top, left: position.left }}
            className="bg-white rounded-lg shadow-xl border border-gray-200 z-[9999] w-64 overflow-hidden animate-in fade-in zoom-in-95 duration-100"
        >
            <div className="p-2 border-b border-gray-100">
                <div className="text-xs font-semibold text-gray-500 mb-2 px-1">Asignar a...</div>
                <div className="relative">
                    <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                    <input
                        type="text"
                        placeholder="Buscar persona..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        autoFocus
                    />
                </div>
            </div>

            <div className="max-h-60 overflow-y-auto py-1">
                {filteredUsers.map(u => {
                    // Check if this user is the current logged-in user
                    // If so, use the auth profile data which is more up-to-date immediately after changes
                    const isCurrentUser = user && (u.email === user.email || u.id === user.uid);
                    const displayName = isCurrentUser ? (user.displayName || u.displayName) : u.displayName;
                    const photoURL = isCurrentUser ? (user.photoURL || u.photoURL) : u.photoURL;

                    return (
                        <button
                            key={u.id}
                            onClick={() => { onSelect(u.email); onClose(); }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-sm transition-colors"
                        >
                            {photoURL ? (
                                <img src={photoURL} alt="" className="w-6 h-6 rounded-full object-cover" />
                            ) : (
                                <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold">
                                    {displayName ? displayName.charAt(0).toUpperCase() : u.email.charAt(0).toUpperCase()}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">{displayName || u.email}</div>
                                {displayName && <div className="text-xs text-gray-500 truncate">{u.email}</div>}
                            </div>
                            {currentAssignee === u.email && (
                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            )}
                        </button>
                    );
                })}

                {filteredUsers.length === 0 && (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        No se encontraron usuarios
                    </div>
                )}
            </div>

            <div className="border-t border-gray-100 p-1">
                <button
                    onClick={() => { onSelect(null); onClose(); }}
                    className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 flex items-center gap-2 text-sm rounded transition-colors"
                >
                    <FiX size={14} />
                    <span>Quitar asignación</span>
                </button>
            </div>
        </div>
    );

    return createPortal(content, document.body);
};

export default UserSelectPopover;
